import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail, API_BASE } from "@/lib/api";
import { formatJPY, formatDate, todayISO } from "@/lib/format";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Plus, MoreHorizontal, FileText, Download, Copy, Trash2, Send, ArrowRightLeft,
  CheckCircle2, Loader2, Layers, ClipboardCheck,
} from "lucide-react";
import { toast } from "sonner";
import DocFormDialog from "@/components/billing/DocFormDialog";
import { DOC_CONFIG, STATUS_LABELS, STATUS_STYLE } from "./config";

function StatusBadge({ status }) {
  return (
    <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_STYLE[status] || "bg-stone-100 text-stone-600"}`}>
      {STATUS_LABELS[status] || status}
    </span>
  );
}

export default function SalesDocPage({ docType }) {
  const cfg = DOC_CONFIG[docType];
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selected, setSelected] = useState([]);
  const [sendDoc, setSendDoc] = useState(null);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeDate, setMergeDate] = useState({ issue_date: todayISO(), due_date: "" });

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/billing/documents/${docType}`)
      .then((r) => setDocs(r.data || []))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [docType]);

  useEffect(() => { load(); setSelected([]); }, [load]);

  const openPdf = async (d) => {
    try {
      const r = await api.get(`/billing/documents/${docType}/${d.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank");
    } catch { toast.error("PDFの生成に失敗しました"); }
  };

  const exportCsv = async () => {
    try {
      const r = await api.get(`/billing/documents/${docType}/export/csv`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${docType}_export.csv`; a.click();
    } catch { toast.error("CSV出力に失敗しました"); }
  };

  const doConvert = async (d, target) => {
    try {
      const r = await api.post(`/billing/documents/${docType}/${d.id}/convert`, { target_type: target });
      toast.success(`${DOC_CONFIG[target].title} ${r.data.doc_number} を作成しました`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const doConfirm = async (d) => {
    try {
      await api.post(`/billing/documents/invoice/${d.id}/confirm`);
      toast.success("請求書を確定し、売上仕訳候補を作成しました");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const doDuplicate = async (d) => {
    try { await api.post(`/billing/documents/${docType}/${d.id}/duplicate`); toast.success("複製しました"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const doDelete = async (d) => {
    if (!window.confirm(`${d.doc_number} を削除しますか？`)) return;
    try { await api.delete(`/billing/documents/${docType}/${d.id}`); toast.success("削除しました"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const setStatus = async (d, status) => {
    try { await api.patch(`/billing/documents/${docType}/${d.id}/status`, { status }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const doInspect = async (d) => {
    try {
      await api.post(`/billing/inspections`, { delivery_id: d.id, inspection_date: todayISO(), result: "passed" });
      toast.success("検収書を作成しました");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const doMerge = async () => {
    if (selected.length < 1) { toast.error("納品書を選択してください"); return; }
    try {
      const r = await api.post(`/billing/invoices/merge`, { delivery_ids: selected, ...mergeDate });
      toast.success(`合算請求書 ${r.data.doc_number} を作成しました`);
      setMergeOpen(false); setSelected([]); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const doSend = async () => {
    try {
      await api.post(`/billing/documents/${docType}/${sendDoc.id}/send`, { method: "email", to: sendDoc._to });
      toast.success("送付履歴を記録しました（メール送信は今後有効化予定）");
      setSendDoc(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const toggleSel = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  return (
    <div data-testid={`billing-${docType}-page`}>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="font-display font-bold text-2xl text-stone-900">{cfg.title}</h1>
          <p className="text-sm text-stone-500 mt-0.5">{docs.length} 件</p>
        </div>
        <div className="flex items-center gap-2">
          {docType === "delivery" && selected.length > 0 && (
            <button onClick={() => setMergeOpen(true)} className="inline-flex items-center gap-1.5 rounded-md border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-100" data-testid="merge-invoice-button">
              <Layers className="h-4 w-4" /> 合算請求 ({selected.length})
            </button>
          )}
          <button onClick={exportCsv} className="inline-flex items-center gap-1.5 rounded-md border border-stone-200 px-3 py-2 text-sm font-medium text-stone-700 hover:bg-stone-50" data-testid="export-csv-button">
            <Download className="h-4 w-4" /> CSV
          </button>
          <button onClick={() => { setEditing(null); setFormOpen(true); }} className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700" data-testid="new-doc-button">
            <Plus className="h-4 w-4" /> 新規作成
          </button>
        </div>
      </div>

      <div className="rounded-xl border border-stone-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 text-stone-500 text-xs uppercase tracking-wide">
              <tr>
                {docType === "delivery" && <th className="px-3 py-3"></th>}
                <th className="px-4 py-3 text-left">番号</th>
                <th className="px-4 py-3 text-left">取引先</th>
                <th className="px-4 py-3 text-left">日付</th>
                <th className="px-4 py-3 text-right">合計</th>
                <th className="px-4 py-3 text-left">ステータス</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {loading ? (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-stone-400"><Loader2 className="h-5 w-5 animate-spin inline" /></td></tr>
              ) : docs.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-stone-400">{cfg.title}はまだありません</td></tr>
              ) : docs.map((d) => (
                <tr key={d.id} className="hover:bg-stone-50/60" data-testid={`doc-row-${d.id}`}>
                  {docType === "delivery" && (
                    <td className="px-3 py-3">
                      <Checkbox checked={selected.includes(d.id)} onCheckedChange={() => toggleSel(d.id)} data-testid={`select-${d.id}`} />
                    </td>
                  )}
                  <td className="px-4 py-3 font-medium text-stone-900">{d.doc_number}</td>
                  <td className="px-4 py-3 text-stone-700">{d.customer_name}</td>
                  <td className="px-4 py-3 text-stone-500">{formatDate(d.issue_date)}</td>
                  <td className="px-4 py-3 text-right font-medium">{formatJPY(d.total)}</td>
                  <td className="px-4 py-3"><StatusBadge status={d.status} /></td>
                  <td className="px-4 py-3 text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger data-testid={`row-menu-${d.id}`}><MoreHorizontal className="h-5 w-5 text-stone-400" /></DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-52">
                        <DropdownMenuItem onClick={() => openPdf(d)}><FileText className="h-4 w-4 mr-2" /> PDF表示・印刷</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => { setEditing(d); setFormOpen(true); }}>編集</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => doDuplicate(d)}><Copy className="h-4 w-4 mr-2" /> 複製</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setSendDoc({ ...d, _to: d.customer_email || "" })}><Send className="h-4 w-4 mr-2" /> 送付</DropdownMenuItem>
                        {cfg.hasConfirm && <DropdownMenuItem onClick={() => doConfirm(d)}><CheckCircle2 className="h-4 w-4 mr-2" /> 確定（仕訳候補作成）</DropdownMenuItem>}
                        {cfg.hasInspection && <DropdownMenuItem onClick={() => doInspect(d)}><ClipboardCheck className="h-4 w-4 mr-2" /> 検収書を作成</DropdownMenuItem>}
                        {cfg.convertTo.length > 0 && <DropdownMenuSeparator />}
                        {cfg.convertTo.map((c) => (
                          <DropdownMenuItem key={c.type} onClick={() => doConvert(d, c.type)}><ArrowRightLeft className="h-4 w-4 mr-2" /> {c.label}</DropdownMenuItem>
                        ))}
                        <DropdownMenuSeparator />
                        <DropdownMenuLabel className="text-xs text-stone-400">ステータス変更</DropdownMenuLabel>
                        {(cfg.title === "見積書" ? ["sent", "accepted", "lost", "canceled"] : ["sent", "canceled"]).map((s) => (
                          <DropdownMenuItem key={s} onClick={() => setStatus(d, s)}>{STATUS_LABELS[s]}</DropdownMenuItem>
                        ))}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => doDelete(d)} className="text-red-600"><Trash2 className="h-4 w-4 mr-2" /> 削除</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <DocFormDialog docType={docType} open={formOpen} onOpenChange={setFormOpen} initial={editing} onSaved={load} />

      {/* Merge dialog */}
      <Dialog open={mergeOpen} onOpenChange={setMergeOpen}>
        <DialogContent data-testid="merge-dialog">
          <DialogHeader><DialogTitle>納品書を合算して請求</DialogTitle></DialogHeader>
          <p className="text-sm text-stone-500">{selected.length} 件の納品書を1枚の請求書にまとめます。</p>
          <div className="grid grid-cols-2 gap-3 mt-2">
            <div><Label>請求日</Label><Input type="date" value={mergeDate.issue_date} onChange={(e) => setMergeDate({ ...mergeDate, issue_date: e.target.value })} /></div>
            <div><Label>支払期限</Label><Input type="date" value={mergeDate.due_date} onChange={(e) => setMergeDate({ ...mergeDate, due_date: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <button onClick={() => setMergeOpen(false)} className="px-4 py-2 text-sm text-stone-600">キャンセル</button>
            <button onClick={doMerge} className="px-5 py-2 rounded-md bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700" data-testid="merge-confirm-button">合算請求書を作成</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send dialog */}
      <Dialog open={!!sendDoc} onOpenChange={(o) => !o && setSendDoc(null)}>
        <DialogContent data-testid="send-dialog">
          <DialogHeader><DialogTitle>{cfg.title}を送付</DialogTitle></DialogHeader>
          <div><Label>送付先メール</Label><Input value={sendDoc?._to || ""} onChange={(e) => setSendDoc({ ...sendDoc, _to: e.target.value })} placeholder="client@example.com" data-testid="send-to-input" /></div>
          <p className="text-[11px] text-stone-400">現在は送付履歴の記録のみ行います。メール送信は今後有効化予定です。</p>
          <DialogFooter>
            <button onClick={() => setSendDoc(null)} className="px-4 py-2 text-sm text-stone-600">キャンセル</button>
            <button onClick={doSend} className="px-5 py-2 rounded-md bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700" data-testid="send-confirm-button">送付を記録</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
